import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import SearchIcon from "@mui/icons-material/Search";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useFeedback } from "./FeedbackProvider";
import { IMAGE_TYPES, PHOTO_MAX_BYTES, compressPhoto, formatBytes } from "utils/uploads";

const PLACEHOLDER = "/Portrait_Placeholder.png";

const emptyValues = { photo: null, name: "", experience: "", qualification: "", subjects: "" };

// InstituteTeachers.js and SchoolTeachers.js were 516 and 517 lines that
// differed only in which three service functions they called and which word
// appeared in the heading. Keeping two copies meant every fix -- the alert()
// calls, the unresponsive two-column grid, the 100KB compression that
// predated utils/uploads.js -- had to be made twice, and in practice drifted.
// MUI's Avatar renders its children when the image fails to load, so a photo
// that 402s or 404s degrades to the teacher's initials rather than an empty
// grey disc.
const initials = (name) =>
  String(name ?? "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("") || "?";

function TeacherManager({ label, data, reload, onCreate, onUpdate, onSoftDelete }) {
  const { notify, confirm } = useFeedback();
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(PLACEHOLDER);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: emptyValues,
    validationSchema: Yup.object().shape({
      photo: isEdit
        ? Yup.mixed()
        : Yup.mixed()
            .required("Photo is required")
            .test(
              "fileSize",
              `Photo must be under ${formatBytes(PHOTO_MAX_BYTES)} after compression`,
              (value) => !value || !(value instanceof File) || value.size <= PHOTO_MAX_BYTES
            )
            .test(
              "fileType",
              "Unsupported file format (only png, jpg, jpeg, webp)",
              (value) => !value || !(value instanceof File) || IMAGE_TYPES.includes(value.type)
            ),
      name: Yup.string().trim().required("Name is required"),
      experience: Yup.number()
        .typeError("Experience must be a number")
        .min(0, "Experience must be at least 0")
        .required("Experience is required"),
      qualification: Yup.string().trim().required("Qualification is required"),
      subjects: Yup.string().trim().required("Subjects or class is required"),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        // A failed write used to reset the form and announce "Submit
        // Successfully" regardless, because nothing checked the outcome.
        if (isEdit) {
          await onUpdate(selected.id, values);
        } else {
          await onCreate(values);
        }
        handleClose();
        notify(isEdit ? `${label} updated.` : `${label} added.`);
        reload();
      } catch (error) {
        console.error("Could not save teacher:", error);
        notify("Could not save the record. Please try again.", "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      validation.setFieldError("photo", "Unsupported format. Upload png, jpg, jpeg or webp.");
      return;
    }

    try {
      const processed = await compressPhoto(file);
      validation.setFieldValue("photo", processed);

      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(processed);
    } catch (error) {
      console.error("Error compressing image:", error);
      validation.setFieldError("photo", "Could not process that image. Please try another.");
    }
  };

  const handleClose = () => {
    setImagePreview(PLACEHOLDER);
    setSelected(null);
    validation.resetForm();
    setOpen(false);
    setIsEdit(false);
  };

  const handleEdit = (teacher) => {
    setSelected(teacher);
    setImagePreview(teacher?.photo || PLACEHOLDER);
    setIsEdit(true);
    setOpen(true);
  };

  const handleDelete = async (teacher) => {
    const confirmed = await confirm({
      title: `Remove ${teacher.name || "this teacher"}?`,
      message: "They will stop appearing on the public site. This can be undone in the database.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await onSoftDelete(teacher.id);
      notify(`${label} removed.`);
      reload();
    } catch (error) {
      console.error("Could not remove teacher:", error);
      notify("Could not remove the record.", "error");
    }
  };

  useEffect(() => {
    validation.setValues({
      photo: selected?.photo || null,
      name: selected?.name || "",
      experience: selected?.experience || "",
      qualification: selected?.qualification || "",
      subjects: selected?.subjects || "",
    });
    setImagePreview(selected?.photo || PLACEHOLDER);
    // `validation` is intentionally out of the dependency list: Formik returns
    // a new object each render, so including it would loop forever.
  }, [selected]);

  const needle = search.trim().toLowerCase();
  const visible = data.filter((teacher) => {
    if (teacher.isDeleted) return false;
    if (!needle) return true;
    return [teacher.name, teacher.subjects, teacher.qualification]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(needle));
  });

  const requiredMark = <span style={{ color: "#f44335", marginLeft: 3 }}>*</span>;

  const field = (name, labelText, extra = {}) => (
    <FormControl fullWidth sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.5 }}>
        {labelText}
        {requiredMark}
      </Typography>
      <TextField
        size="small"
        name={name}
        value={validation.values[name]}
        onChange={validation.handleChange}
        onBlur={validation.handleBlur}
        error={Boolean(validation.touched[name] && validation.errors[name])}
        {...extra}
      />
      {validation.touched[name] && validation.errors[name] ? (
        <FormHelperText sx={{ color: "#f44335" }}>{validation.errors[name]}</FormHelperText>
      ) : null}
    </FormControl>
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder={`Search ${label.toLowerCase()}s`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: { sm: 260 }, bgcolor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ textTransform: "none", color: "#fff" }}
          onClick={() => setOpen(true)}
        >
          Add {label.toLowerCase()}
        </Button>
      </Stack>

      {visible.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: "center", color: "#7b809a" }}>
          {data.length === 0 ? `No ${label.toLowerCase()}s yet.` : "No matches for that search."}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {visible.map((teacher) => (
            // The old grid was a hard xs={6}, so on a phone every card was
            // half a screen wide with the name wrapping to four lines.
            <Grid item xs={12} md={6} xl={4} key={teacher.id}>
              <Card
                variant="outlined"
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  height: "100%",
                  minHeight: 148,
                  overflow: "hidden",
                  borderRadius: 2,
                  border: "1px solid #e3e8f0",
                }}
              >
                <Avatar
                  src={teacher.photo}
                  alt={teacher.name}
                  variant="square"
                  sx={{
                    width: 118,
                    height: "auto",
                    flexShrink: 0,
                    bgcolor: "#174276",
                    fontSize: "1.4rem",
                  }}
                >
                  {initials(teacher.name)}
                </Avatar>
                <Box sx={{ p: 1.5, minWidth: 0, flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1b2a41" }}>
                      {teacher.name}
                    </Typography>
                    <Stack direction="row" sx={{ flexShrink: 0 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(teacher)}>
                          <DriveFileRenameOutlineIcon fontSize="small" sx={{ color: "#174276" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleDelete(teacher)}>
                          <DeleteForeverIcon fontSize="small" sx={{ color: "#f44335" }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Typography sx={{ fontSize: "0.8rem", color: "#7b809a", mt: 0.5 }}>
                    <strong>Subjects:</strong> {teacher.subjects}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#7b809a" }}>
                    <strong>Experience:</strong> {teacher.experience}
                  </Typography>
                  {teacher.qualification && (
                    <Chip label={teacher.qualification} size="small" sx={{ mt: 1 }} />
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box sx={{ width: { xs: "100vw", sm: 420 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2.5, py: 2, bgcolor: "#123358", color: "#fff" }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              {isEdit ? `Edit ${label.toLowerCase()}` : `Add ${label.toLowerCase()}`}
            </Typography>
            <IconButton onClick={handleClose} sx={{ color: "#fff" }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={{ p: 2.5 }}>
            <form onSubmit={validation.handleSubmit} noValidate>
              {submitting ? (
                <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  <FormControl fullWidth>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Teacher"
                        sx={{
                          width: 92,
                          height: 92,
                          objectFit: "cover",
                          borderRadius: 2,
                          border: "1px solid #e3e8f0",
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                          Photo{!isEdit && requiredMark}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "#7b809a", mb: 0.5 }}>
                          png, jpg, jpeg or webp. Large photos are compressed to{" "}
                          {formatBytes(PHOTO_MAX_BYTES)} automatically.
                        </Typography>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.webp"
                          onChange={handleImageChange}
                          style={{ fontSize: "0.78rem", maxWidth: "100%" }}
                        />
                      </Box>
                    </Stack>
                    {validation.touched.photo && validation.errors.photo ? (
                      <FormHelperText sx={{ color: "#f44335" }}>
                        {validation.errors.photo}
                      </FormHelperText>
                    ) : null}
                  </FormControl>

                  {field("name", "Name")}
                  {field("experience", "Experience (in years)")}
                  {field("qualification", "Qualification")}
                  {field("subjects", "Class and subjects to be taught")}

                  <Divider sx={{ mt: 4 }} />
                  <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                    <Button fullWidth variant="outlined" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button fullWidth type="submit" variant="contained" sx={{ color: "#fff" }}>
                      {isEdit ? "Save changes" : "Add"}
                    </Button>
                  </Stack>
                </Box>
              )}
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

TeacherManager.propTypes = {
  label: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  reload: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onSoftDelete: PropTypes.func.isRequired,
};

export default TeacherManager;
